import {
    Box,
    Button,
    CardActionArea,
    Typography,
    useTheme
} from '@mui/material'
import {
    Filter,
    FlexBox,
    FlexBoxColumn,
    FlexBoxSpaceBetween
} from 'components/atoms'
import { RunnerOption } from 'components/molecules'
import { useBetContext } from 'contexts/BetContext'
import { useTranslation } from 'contexts/TranslationContext'
import { useMemo, useState } from 'react'
import { getBetCount, parseBetDesignationArray } from 'utils'

export const RunnerSelector = () => {
    const theme = useTheme()
    const translation = useTranslation()

    const { setRunnerSelectionVisible } = useBetContext()

    const {
        bets,
        selectedBets,
        availableRunners,
        disabledRunners,
        selectedBetType,
        addHorseSelection,
        removeHorseSelection,
        selectedFormulaKey,
        handleTrackingEvent,
        mainColor
    } = useBetContext()

    const { base, associates } = useMemo(
        () =>
            parseBetDesignationArray(
                Array.from(selectedBets.values() || []),
                selectedBetType
            ),
        [bets, selectedBetType]
    )

    const betCount = useMemo(
        () =>
            getBetCount({
                bets,
                selectedBets,
                selectedBetType,
                selectedFormulaKey,
                availableRunners
            }),
        [bets, selectedBetType, selectedFormulaKey]
    )

    const isExtendedDisplay =
        selectedBetType !== 'S' && selectedFormulaKey !== 'UNIT'

    const [selectedFilter, setSelectedFilter] = useState<'NUMBER' | 'ODD'>(
        'NUMBER'
    )

    const sortedRunners = useMemo(() => {
        const runners = [...availableRunners, ...disabledRunners]
        if (selectedFilter === 'NUMBER') {
            return runners.sort((a, b) => (a.saddle > b.saddle ? 1 : -1))
        }
        return runners.sort((a, b) => (a.liveOdd > b.liveOdd ? 1 : -1))
    }, [availableRunners, disabledRunners, selectedFilter])

    const handleFilterClick = (filter: 'NUMBER' | 'ODD') => {
        setSelectedFilter(filter)
        void handleTrackingEvent({
            eventType: 'RUNNERS',
            actionType: `SORT_${filter}`
        })
    }

    return (
        <FlexBoxColumn
            sx={{
                width: '100%',
                backgroundColor: 'white',
                paddingBottom: availableRunners.length ? 15 : 0
            }}
        >
            <FlexBoxColumn
                sx={{
                    width: '100%',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    position: 'fixed',
                    top: 0,
                    backgroundColor: 'white',
                    pt: 1,
                    zIndex: 2
                }}
            >
                <FlexBoxSpaceBetween
                    sx={{
                        width: `calc(100% - ${theme.spacing(4)})`,
                        minHeight: '20px',
                        mt: 2
                    }}
                >
                    <Typography
                        variant="title-16-bold"
                        sx={{
                            pl: 2,
                            color: theme.palette.text.secondary,
                            fontSize: 20
                        }}
                    >
                        {translation.chooseHorses}
                    </Typography>
                    <CardActionArea
                        sx={{ width: 'fit-content' }}
                        onClick={() => setRunnerSelectionVisible(false)}
                    >
                        <Typography sx={{ color: mainColor }}>
                            Annuler
                        </Typography>
                    </CardActionArea>
                </FlexBoxSpaceBetween>

                <FlexBoxSpaceBetween sx={{ width: '100%', mt: 3 }}>
                    <Filter
                        title="N°"
                        sx={{ ml: 1.5 }}
                        isEnabled={selectedFilter === 'NUMBER'}
                        onClick={() => handleFilterClick('NUMBER')}
                    />
                    <FlexBox>
                        <Filter
                            title={translation.odd}
                            isEnabled={selectedFilter === 'ODD'}
                            onClick={() => handleFilterClick('ODD')}
                            sx={{ mr: isExtendedDisplay ? 2 : 6 }}
                        />
                        {isExtendedDisplay && (
                            <>
                                <Typography
                                    variant="text-14-regular"
                                    sx={{
                                        width: 30,
                                        color: theme.palette.text.secondary
                                    }}
                                >
                                    B.
                                </Typography>
                                <Typography
                                    variant="text-14-regular"
                                    sx={{
                                        width: 30,
                                        mr: 0.5,
                                        color: theme.palette.text.secondary
                                    }}
                                >
                                    A.
                                </Typography>
                            </>
                        )}
                    </FlexBox>
                </FlexBoxSpaceBetween>
            </FlexBoxColumn>
            <Box sx={{ my: 6 }} />
            {sortedRunners.map((runner) => {
                const hNum = runner.saddle.toString()
                return (
                    <Box
                        key={`runner-option-${runner.saddle}`}
                        sx={{ width: `100%`, m: 0.5 }}
                    >
                        <RunnerOption
                            associateVisible={
                                selectedBetType !== 'S' &&
                                selectedFormulaKey !== 'UNIT'
                            }
                            baseChecked={base.includes(hNum)}
                            onBaseChange={(checked) => {
                                void handleTrackingEvent({
                                    eventType: 'RUNNERS',
                                    actionType: checked ? 'ADD' : 'REMOVE'
                                })
                                if (!checked) {
                                    return removeHorseSelection(hNum)
                                }
                                addHorseSelection({
                                    horseNumber: hNum,
                                    odd: runner.liveOdd,
                                    type: 'BASE'
                                })
                            }}
                            associateChecked={
                                (selectedFormulaKey === 'WHEEL' &&
                                    !base.includes(hNum)) ||
                                associates.includes(hNum)
                            }
                            onAssociateChange={(checked) => {
                                void handleTrackingEvent({
                                    eventType: 'RUNNERS',
                                    actionType: checked ? 'ADD' : 'REMOVE'
                                })
                                if (!checked) {
                                    return removeHorseSelection(hNum)
                                }
                                addHorseSelection({
                                    horseNumber: hNum,
                                    odd: runner.liveOdd,
                                    type: 'ASSOCIATE'
                                })
                            }}
                            associateDisabled={selectedFormulaKey === 'WHEEL'}
                            {...runner}
                        />
                    </Box>
                )
            })}
            {selectedFormulaKey !== 'UNIT' &&
            betCount === 0 &&
            (base.length || associates.length) ? (
                <Typography
                    sx={{
                        mt: 2,
                        color: theme.palette.error.light,
                        textAlign: 'center'
                    }}
                    variant="text-13-semi-bold"
                >
                    {base.length === 0 ? translation.missingBase : ''}
                </Typography>
            ) : null}
            <FlexBox
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    py: 3,
                    justifyContent: 'center',
                    backgroundColor: 'white',
                    zIndex: 2
                }}
            >
                <Button
                    onClick={() => {
                        setRunnerSelectionVisible(false)
                        void handleTrackingEvent({
                            eventType: 'RUNNERS',
                            actionType: 'CLOSE'
                        })
                    }}
                    sx={{
                        textTransform: 'uppercase',
                        fontWeight: 'bold',
                        width: '80%',
                        borderRadius: theme.spacing(1),
                        backgroundColor: mainColor
                    }}
                    variant="contained"
                >
                    {translation.finish}
                </Button>
            </FlexBox>
        </FlexBoxColumn>
    )
}
